import { useState } from 'react';
import { Anchor, Avatar, Box, Group, Stack, Tooltip } from '@mantine/core';
import moment from 'moment';
import Text from '@ui/components/Text/Text';
import Icon from '@ui/components/Icon/Icon';
import AssignSeatModal from '../../modals/AssignSeatModal/AssignSeatModal';
import { NonMemberJSON, NON_MEMBER_ROLES } from '../../types';

type Props = {
	workspaceID: number;
	nonMember: NonMemberJSON;
	isOwner: boolean;
	onMemberAdded: () => void;
	onRequestSeat: ( nonMember: NonMemberJSON ) => void;
	isRequestingSeat: boolean;
};

const RoleTag = ( { nonMember, isOwner }: { nonMember: NonMemberJSON; isOwner: boolean } ) => {
	const isExpired = nonMember.role === NON_MEMBER_ROLES.EDITOR_EXPIRED;

	const tooltipText = isExpired
		? `This user's Editor access has ended. Some features like managing assets and adding rounds are no longer available.${isOwner ? '' : ' Request a seat.'}`
		: `This user has a temporary editing permission available.${isOwner ? '' : ' Request a seat.'}`;

	const label = isExpired
		? 'Editor Expired'
		: `Full Editor (${nonMember.days_remaining} day${nonMember.days_remaining === 1 ? '' : 's'} left)`;

	return (
		<Tooltip label={tooltipText} withArrow multiline width={220}>
			<Box
				sx={theme => ( {
					cursor: 'default',
					display: 'inline-flex',
					alignItems: 'center',
					gap: 2,
					padding: '4px',
					borderRadius: 4,
					backgroundColor: isExpired ? theme.colors.orange[ 0 ] : theme.colors.blue[ 0 ]
				} )}
			>
				<Text
					variant="caption-semibold"
					color={isExpired ? 'orange.6' : 'blue.5'}
					style={{ whiteSpace: 'nowrap' }}
				>
					{label}
				</Text>
				<Icon
					icon={isExpired ? 'warning' : 'info_outlined'}
					color={isExpired ? 'orange.6' : 'blue.5'}
					size="xs"
				/>
			</Box>
		</Tooltip>
	);
};

const NonMemberRow = ( {
	workspaceID, nonMember, isOwner, onMemberAdded, onRequestSeat, isRequestingSeat
}: Props ) => {
	const [ showAssignModal, setShowAssignModal ] = useState( false );

	const handleAction = () => {
		if ( isOwner ) {
			setShowAssignModal( true );
		} else {
			onRequestSeat( nonMember );
		}
	};

	return (
		<>
			<Group spacing={2} w="100%" position="apart" noWrap style={{ paddingLeft: 6 }}>
				<Box style={{ flex: 1, overflow: 'hidden' }}>
					<Group spacing={16} position="left" noWrap>
						<Avatar src={nonMember.avatar_url} radius="xl" size={34}>
							{nonMember.name.charAt( 0 ).toUpperCase()}
						</Avatar>
						<Stack spacing={0} style={{ minWidth: 0 }}>
							<Text style={{ fontSize: 14, fontWeight: 600, color: 'var(--color-text-primary)', letterSpacing: 0 }}>{nonMember.name}</Text>
							<Text style={{ fontSize: 14, fontWeight: 400, letterSpacing: '0.14px', color: 'var(--color-text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{nonMember.email}</Text>
						</Stack>
					</Group>
				</Box>

				<Box w="180px" style={{ display: 'flex', justifyContent: 'flex-end', paddingRight: 16 }}>
					<RoleTag nonMember={nonMember} isOwner={isOwner} />
				</Box>

				<Box w="120px">
					<Text style={{ fontSize: 12, fontWeight: 400, lineHeight: '18px', letterSpacing: '0.13px', color: 'var(--color-text-secondary)' }}>
						{nonMember.last_active_at ? moment( nonMember.last_active_at ).format( 'DD/MM/YYYY' ) : '—'}
					</Text>
				</Box>

				<Box w="120px">
					<Text style={{ fontSize: 12, fontWeight: 400, lineHeight: '18px', letterSpacing: '0.13px', color: 'var(--color-text-secondary)' }}>
						{nonMember.collaborations_count > 0
							? `${nonMember.collaborations_count} Project${nonMember.collaborations_count > 1 ? 's' : ''}`
							: '—'}
					</Text>
				</Box>

				<Box w="120px">
					<Anchor
						component="button"
						onClick={handleAction}
						color="blue.6"
						disabled={!isOwner && isRequestingSeat}
						style={{ fontSize: 12, fontWeight: 700, lineHeight: '18px', letterSpacing: '0.13px', textDecoration: 'underline' }}
					>
						{isOwner ? 'Assign a Seat' : 'Request a Seat'}
					</Anchor>
				</Box>
			</Group>

			{showAssignModal && (
				<AssignSeatModal
					workspaceID={workspaceID}
					nonMember={nonMember}
					onClose={() => setShowAssignModal( false )}
					onSuccess={onMemberAdded}
				/>
			)}
		</>
	);
};

export default NonMemberRow;
